import { useState, useCallback, useEffect } from 'react';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import { criaCookieToken, getConfigAmbienteDominio } from 'services/authentication/auth';
import { criarRemessa, getContasCobranca, getFiltroEmpresas } from '../../../../api/remessas';

const getDadosRemessaSessionStorage = () => {
  try {
    const dadosRemessa = sessionStorage.getItem('dados_remessa');

    if (!dadosRemessa) return null;

    const dadosParseados = JSON.parse(dadosRemessa);
    const { codEmpresa, codContaCobranca } = dadosParseados ?? {};

    if (!codEmpresa || !codContaCobranca) return null;

    return { codEmpresa, codContaCobranca };
  } catch {
    return null;
  }
};

const removeDadosRemessaSessionStorage = () => {
  sessionStorage.removeItem('dados_remessa');
};

const useModalNovaRemessaHook = ({ onFechar, abrirModal, onRemessaCriada }) => {
  const [empresas, setEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [contaCobranca, setContaCobranca] = useState('');
  const [manterSacados, setManterSacados] = useState(false);
  const [naoProtestar, setNaoProtestar] = useState(false);
  const [trocaCarteira, setTrocaCarteira] = useState(false);
  const [baixarTitulos, setBaixarTitulos] = useState(false);
  const [permissoesConta, setPermissoesConta] = useState({});
  const [contasDisponiveis, setContasDisponiveis] = useState([]);
  const [criando, setCriando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(false);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [dadosRemessaRestauracao, setDadosRemessaRestauracao] = useState(null);

  useEffect(() => {
    setCarregandoEmpresas(true);
    getFiltroEmpresas()
      .then(response => setEmpresas(response.data.data ?? []))
      .catch(() => openSnackbarMain('Erro ao carregar empresas', 'error'))
      .finally(() => setCarregandoEmpresas(false));
  }, []);

  useEffect(() => {
    if (empresaSelecionada) {
      setCarregandoContas(true);
      getContasCobranca(empresaSelecionada)
        .then(response => setContasDisponiveis(response.data.data.contas_cobranca ?? []))
        .catch(() => {
          setContasDisponiveis([]);
          openSnackbarMain('Erro ao carregar contas de cobrança', 'error');
        })
        .finally(() => setCarregandoContas(false));
      setContaCobranca('');
    } else {
      setContasDisponiveis([]);
      setContaCobranca('');
    }
  }, [empresaSelecionada]);

  useEffect(() => {
    const dadosRemessa = getDadosRemessaSessionStorage();

    if (!dadosRemessa) return;

    setDadosRemessaRestauracao(dadosRemessa);
    abrirModal();
  }, [abrirModal]);

  useEffect(() => {
    if (!dadosRemessaRestauracao?.codEmpresa || carregandoEmpresas || empresas.length === 0) return;
    if (empresaSelecionada === dadosRemessaRestauracao.codEmpresa) return;

    const empresaExisteNaLista = empresas.some(
      empresa => String(empresa.codigo) === String(dadosRemessaRestauracao.codEmpresa)
    );

    if (!empresaExisteNaLista) {
      removeDadosRemessaSessionStorage();
      setDadosRemessaRestauracao(null);
      return;
    }

    setEmpresaSelecionada(dadosRemessaRestauracao.codEmpresa);
  }, [carregandoEmpresas, dadosRemessaRestauracao, empresas, empresaSelecionada]);

  useEffect(() => {
    if (!dadosRemessaRestauracao?.codContaCobranca || !empresaSelecionada || carregandoContas) return;
    if (String(empresaSelecionada) !== String(dadosRemessaRestauracao.codEmpresa)) return;
    if (contaCobranca === dadosRemessaRestauracao.codContaCobranca) return;
    if (contasDisponiveis.length === 0) return;

    const contaExisteNaLista = contasDisponiveis.some(
      conta => String(conta.codigo) === String(dadosRemessaRestauracao.codContaCobranca)
    );

    if (!contaExisteNaLista) {
      removeDadosRemessaSessionStorage();
      setDadosRemessaRestauracao(null);
      return;
    }

    setContaCobranca(dadosRemessaRestauracao.codContaCobranca);
    removeDadosRemessaSessionStorage();
    setDadosRemessaRestauracao(null);
  }, [carregandoContas, contaCobranca, contasDisponiveis, dadosRemessaRestauracao, empresaSelecionada]);

  useEffect(() => {
    if (!contaCobranca) {
      setPermissoesConta({});
      setNaoProtestar(false);
      setTrocaCarteira(false);
      setBaixarTitulos(false);
      setManterSacados(false);
      return;
    }

    const contaSelecionada = contasDisponiveis.find(
      conta => String(conta.codigo) === String(contaCobranca)
    );

    if (contaSelecionada) {
      setPermissoesConta({
        permite_nao_protestar: contaSelecionada.permite_nao_protestar ?? false,
        permite_troca_carteira: contaSelecionada.permite_troca_carteira ?? false,
        permite_baixar_titulos: contaSelecionada.permite_baixar_titulos ?? false,
        permite_manter_sacados_originais: contaSelecionada.permite_manter_sacados_originais ?? false
      });
    } else {
      setPermissoesConta({});
    }

    setNaoProtestar(false);
    setTrocaCarteira(false);
    setBaixarTitulos(false);
    setManterSacados(false);
  }, [contaCobranca, contasDisponiveis]);

  const podeCriar = empresaSelecionada !== '' && contaCobranca !== '';

  const handleCriarRemessa = useCallback(
    async (contaCobrancaSelecionada, tipoRemessa) => {
      if (!podeCriar || criando) return;

      setCriando(true);

      try {
        const tipoRemessaFinal = baixarTitulos ? 'T' : tipoRemessa;

        const payloadCriarRemessa = {
          cod_conta_cobranca: contaCobrancaSelecionada,
          tipo_remessa: tipoRemessaFinal,
          baixar_titulos: baixarTitulos ? 'S' : null,
          manter_sacado: manterSacados ? 'S' : null,
          troca_carteira: trocaCarteira ? 'S' : null,
          nao_protestar: naoProtestar ? 'S' : null
        };

        const response = await criarRemessa(payloadCriarRemessa);
        const codRemessa = response?.data?.data?.codRemessa;

        if (!codRemessa) {
          throw new Error('Codigo da remessa nao retornado.');
        }

        if (!criaCookieToken()) {
          window.location.replace('/force-logout');
          return;
        }

        const dominioUrl = getConfigAmbienteDominio();
        const url = `${process.env.REACT_APP_ISEVEN_BASE_URL}/AuthKeyCloack.php?url=/mod/eTes/altremessabanco.php?CODREMESSA=${codRemessa}`;
        window.open(url.replace('TENANT', dominioUrl), '_blank');

        openSnackbarMain('Remessa criada com sucesso!', 'success');
        setSucesso(true);
        onFechar();
        onRemessaCriada?.();
      } catch (erro) {
        openSnackbarMain('Erro ao criar remessa.', 'error');
      } finally {
        setCriando(false);
      }
    },
    [
      podeCriar,
      criando,
      empresaSelecionada,
      contaCobranca,
      manterSacados,
      naoProtestar,
      trocaCarteira,
      baixarTitulos,
      onFechar
    ]
  );

  const handleChangeEmpresa = useCallback(evento => {
    setEmpresaSelecionada(evento.target.value);
  }, []);

  const handleChangeContaCobranca = useCallback(evento => {
    setContaCobranca(evento.target.value);
  }, []);

  const handleChangeManterSacados = useCallback(evento => {
    setManterSacados(evento.target.checked);
  }, []);

  const handleChangeNaoProtestar = useCallback(evento => {
    setNaoProtestar(evento.target.checked);
  }, []);

  const handleChangeTrocaCarteira = useCallback(evento => {
    setTrocaCarteira(evento.target.checked);
  }, []);

  const handleChangeBaixarTitulos = useCallback(evento => {
    setBaixarTitulos(evento.target.checked);
  }, []);

  const resetarEstado = useCallback(() => {
    setEmpresaSelecionada('');
    setContaCobranca('');
    setManterSacados(false);
    setNaoProtestar(false);
    setTrocaCarteira(false);
    setBaixarTitulos(false);
    setPermissoesConta({});
    setContasDisponiveis([]);
    setCriando(false);
    setSucesso(false);
    setCarregandoContas(false);
  }, []);

  return {
    empresas,
    empresaSelecionada,
    contaCobranca,
    manterSacados,
    naoProtestar,
    trocaCarteira,
    baixarTitulos,
    permissoesConta,
    contasDisponiveis,
    criando,
    sucesso,
    podeCriar,
    carregandoEmpresas,
    carregandoContas,
    handleCriarRemessa,
    handleChangeEmpresa,
    handleChangeContaCobranca,
    handleChangeManterSacados,
    handleChangeNaoProtestar,
    handleChangeTrocaCarteira,
    handleChangeBaixarTitulos,
    resetarEstado
  };
};

export default useModalNovaRemessaHook;
